/**
 * PublicResult.js - Public Result Viewer
 * 
 * Displays shared results without requiring authentication.
 * Recipients can view the full result context from a share link.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getBackendUrl } from '../utils/apiUrl';

const BACKEND_URL = getBackendUrl();

export const PublicResult = ({ Header, Footer }) => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPublicResult = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/results/public/${sessionId}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('This result was not found or has expired.');
          } else {
            setError('Failed to load result. Please try again later.');
          }
          setLoading(false);
          return;
        }
        
        const data = await response.json();
        setResult(data);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch public result:', err);
        setError('Failed to load result. Please check your connection.');
        setLoading(false);
      }
    };

    if (sessionId) {
      fetchPublicResult();
    }
  }, [sessionId]);

  const getModeTitle = (mode) => {
    switch (mode) {
      case 'bug_fixing': return '🔧 Bug Fix Solution';
      default: return '🚀 New Project Blueprint';
    }
  };

  const getModeColor = (mode) => {
    switch (mode) {
      case 'bug_fixing': return 'rgba(255, 77, 0, 0.3)';
      default: return 'rgba(139, 92, 246, 0.3)';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        {Header && <Header />}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading shared result...</p>
          </div>
        </div>
        {Footer && <Footer />}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white">
        {Header && <Header />}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="text-6xl mb-4">😕</div>
            <h2 className="text-2xl font-bold mb-4">Result Not Found</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
        {Footer && <Footer />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {Header && <Header />}
      
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header Banner */}
        <div 
          className="mb-6 p-6 rounded-2xl border"
          style={{ 
            background: '#000000', 
            borderColor: getModeColor(result?.mode) 
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">{getModeTitle(result?.mode)}</h1>
              <p className="text-gray-400 text-sm">
                Shared Result • Generated with ElonPrompt AI
              </p>
            </div>
            <div className="px-4 py-2 bg-green-600/20 rounded-lg border border-green-500/30">
              <span className="text-green-400 text-sm font-medium">📤 Public View</span>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-6">
          {/* Plain English Section */}
          {result?.plain_english && (
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
              <h2 className="text-lg font-semibold mb-4 text-purple-400">📝 Plain English</h2>
              <div className="prose prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-gray-300 font-sans text-sm leading-relaxed">
                  {result.plain_english}
                </pre>
              </div>
            </div>
          )}

          {/* Magic Code Section */}
          {result?.magic_code && (
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
              <h2 className="text-lg font-semibold mb-4 text-blue-400">💻 Magic Code</h2>
              <div className="prose prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-gray-300 font-mono text-sm leading-relaxed bg-black/50 p-4 rounded-lg">
                  {result.magic_code}
                </pre>
              </div>
            </div>
          )}

          {/* Requirement Workflow Section */}
          {result?.workflow_summary && (
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
              <h2 className="text-lg font-semibold mb-4 text-green-400">📋 Requirement Workflow</h2>
              <div className="prose prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-gray-300 font-sans text-sm leading-relaxed">
                  {result.workflow_summary}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Call to Action */}
        <div className="mt-8 text-center p-6 bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-xl border border-purple-500/30">
          <h3 className="text-xl font-semibold mb-3">Want to create your own?</h3>
          <p className="text-gray-400 mb-4">Generate AI-powered prompts for your projects</p>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors"
          >
            Try ElonPrompt AI Free →
          </button>
        </div>
      </div>

      {Footer && <Footer />}
    </div>
  );
};

export default PublicResult;
